import React, { useState, useEffect } from 'react'
import Logo from '../../images/logo.png'
import LogoDark from '../../images/logo-dark.png'
import Head from '../../layout/head/Head'
import AuthFooter from './AuthFooter'
import {
  Block,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  PreviewCard
} from '../../components/Component'
import { Link } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import McDataServices from '../../services/McDataServices'
import { useGlobalStoreData } from '../../store/globalStore'
import './ForgotPassword.css'

const STATUS = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR'
}

const ForgotPassword = () => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sentEmail, setSentEmail] = useState('')
  const [status, setStatus] = useState(STATUS.IDLE)
  const [countdown, setCountdown] = useState(0)
  const languageGSD = useGlobalStoreData.language()

  useEffect(() => {
    let timer
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  const handleReset = async (e) => {
    if (e) e.preventDefault()
    if (!isValidEmail(email)) return

    setStatus(STATUS.LOADING)
    try {
      await McDataServices.sendResetPasswordMail(email)
      setSentEmail(email)
      setStatus(STATUS.SUCCESS)
      setCountdown(60)
    } catch (error) {
      console.error('Reset password error:', error)
      setStatus(STATUS.ERROR)
    }
  }

  const handleEmailChange = (event) => {
    setEmail(event.target.value)
    if (status === STATUS.ERROR) setStatus(STATUS.IDLE)
  }

  const isValidEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,24}$/
    return emailPattern.test(email)
  }

  const renderForm = () => (
    <>
      <BlockHead>
        <BlockContent>
          <BlockTitle tag="h5" className="text-center">{t('reset_password_header')}</BlockTitle>
          <BlockDes className="text-center">
            <p>{t('reset_password_desc')}</p>
          </BlockDes>
        </BlockContent>
      </BlockHead>
      <form onSubmit={handleReset}>
        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="emailForReset">
              {t('email')}
            </label>
          </div>
          <div className="form-control-wrap">
            <div className="form-icon form-icon-left">
              <Icon name="mail"/>
            </div>
            <input
              type="email"
              className={`form-control form-control-lg ${status === STATUS.ERROR ? 'error' : ''}`}
              id="emailForReset"
              placeholder={t('email')}
              value={email}
              onChange={handleEmailChange}
              required
            />
          </div>
          {status === STATUS.ERROR && (
            <span className="text-danger fs-12px mt-1">{t('error_sending_email')}</span>
          )}
        </div>
        <div className="form-group">
          <Button
            type="submit"
            color="primary"
            size="lg"
            className="btn-block btn-premium"
            disabled={status === STATUS.LOADING || !isValidEmail(email)}
          >
            {status === STATUS.LOADING
              ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                )
              : (
                  t('send_reset_link')
                )}
          </Button>
        </div>
      </form>
      <div className="form-note-s2 text-center pt-4">
        <Link to={`${process.env.PUBLIC_URL}/${languageGSD}/login`}>
          <strong>{t('return_to_login')}</strong>
        </Link>
      </div>
    </>
  )

  const renderSuccess = () => (
    <div className="success-view">
      <div className="success-icon-wrapper">
        <Icon name="emails"/>
      </div>
      <BlockTitle tag="h4">{t('check_your_email')}</BlockTitle>
      <BlockDes className="mb-4">
        <p>
          <Trans
            i18nKey="reset_email_sent_dest"
            values={{ email: sentEmail }}
            components={[<span className="email-highlight" key="0"/>]}
          />
        </p>
        <p className="text-soft fs-13px">
          {t('spam_folder_notice')}
        </p>
      </BlockDes>
      <div className="form-group">
        <Link to={`${process.env.PUBLIC_URL}/${languageGSD}/login`} className="btn btn-lg btn-outline-light btn-block">
          {t('return_to_login')}
        </Link>
      </div>
      <div className="mt-4 text-center">
        <p className="fs-13px text-soft">
          {t('didnt_receive_email')}{' '}
          {countdown > 0
            ? (
              <span className="text-primary font-weight-bold">{t('resend_in')} {countdown}s</span>
              )
            : (
              <span className="resend-link" onClick={handleReset}>{t('resend')}</span>
              )}
        </p>
      </div>
    </div>
  )

  return (
    <>
      <Head title={t('no_password')}/>
      <Block className="nk-block-middle nk-auth-body wide-xs forgot-password-container">
        <div className="brand-logo pb-4 text-center brand-logo-modern">
          <Link to={process.env.PUBLIC_URL + '/'} className="logo-link">
            <img className="logo-light logo-img logo-img-lg" src={Logo} alt="logo"/>
            <img className="logo-dark logo-img logo-img-lg" src={LogoDark} alt="logo-dark"/>
          </Link>
        </div>
        <PreviewCard className="auth-card-modern" bodyClass="card-inner-lg">
          {status === STATUS.SUCCESS ? renderSuccess() : renderForm()}
        </PreviewCard>
      </Block>
      <AuthFooter/>
    </>
  )
}
export default ForgotPassword
